// BUILD-039 real ListUsers pagination for the Settings > User Management > Directory.
//
// Pure (no React, no fetch, no import.meta.env) so the exact loop the screen runs is testable under
// plain Node. SettingsPage.tsx supplies a `fetchPage` that calls GET /admin/users; this module owns
// continuation, the bound on provider work, de-duplication and the honest "is this complete?" answer.
//
// WHAT "COMPLETE" MEANS
//   complete === true only when the server said there is no further page (nextToken null/absent on a
//   pagination-aware server) or, for a server that predates pagination, it returned fewer than one
//   full page. Anything else is INCOMPLETE and says why. The caller must never present an incomplete
//   list as the whole directory.
//
// BOUNDS
//   * At most MAX_LOGIN_PAGES pages per load (each page is one Cognito ListUsers of up to 60 plus the
//     group lookups the server already did before). 50 pages = up to 3,000 logins.
//   * A repeated continuation token stops the load (a provider/server loop can never spin forever).
//   * Pages are fetched strictly one after another; there is no parallel fan-out.
//
// FAILURE SHAPE
//   * The FIRST page failing throws, so the screen keeps its existing "Could not load users" + Retry.
//   * A LATER page failing returns what was loaded so far with complete=false, reason
//     'continuation-failed' and the error text. Nothing already loaded is thrown away.

/** Cognito ListUsers returns at most 60 per page; also the legacy (pre-pagination) response size. */
export const LOGIN_PAGE_SIZE = 60;
/** Upper bound on pages requested per load (50 x 60 = 3,000 logins). */
export const MAX_LOGIN_PAGES = 50;

export interface LoginPage<T> {
  users: T[];
  /** string = more pages; null = last page; undefined = server predates pagination. */
  nextToken?: string | null;
}

export type IncompleteReason =
  | 'continuation-failed' // a page after the first could not be read
  | 'page-limit'          // reached MAX_LOGIN_PAGES with more still available
  | 'repeated-token'      // the server handed back a token we already used
  | 'server-not-paginated'; // old server returned a full page and cannot say whether there is more

export interface LoadResult<T> {
  users: T[];
  complete: boolean;
  reason?: IncompleteReason;
  /** Error text when reason === 'continuation-failed'. */
  error?: string;
  pages: number;
  duplicatesDropped: number;
  /** True when shouldAbort() ended the load early; the result must be discarded by the caller. */
  aborted: boolean;
}

export interface LoadOptions {
  maxPages?: number;
  /** Called after every page that was read successfully. */
  onProgress?: (loaded: number, pages: number) => void;
  /** Polled between pages; return true to stop (the screen was reloaded or left). */
  shouldAbort?: () => boolean;
}

export async function loadAllLogins<T extends { username: string }>(
  fetchPage: (paginationToken?: string) => Promise<LoginPage<T>>,
  opts: LoadOptions = {},
): Promise<LoadResult<T>> {
  const maxPages = opts.maxPages ?? MAX_LOGIN_PAGES;
  const byUsername = new Map<string, T>();
  const usedTokens = new Set<string>();
  let duplicatesDropped = 0;
  let pages = 0;
  let token: string | undefined;

  for (;;) {
    let page: LoginPage<T>;
    try {
      page = await fetchPage(token);
    } catch (err: any) {
      if (pages === 0) throw err; // first page: let the screen show its normal error + Retry
      return {
        users: [...byUsername.values()], complete: false, reason: 'continuation-failed',
        error: String(err?.message || err || 'unknown error'), pages, duplicatesDropped, aborted: false,
      };
    }
    pages += 1;
    for (const u of page.users || []) {
      if (byUsername.has(u.username)) duplicatesDropped += 1;
      else byUsername.set(u.username, u);
    }
    opts.onProgress?.(byUsername.size, pages);
    if (opts.shouldAbort?.()) {
      return { users: [...byUsername.values()], complete: false, pages, duplicatesDropped, aborted: true };
    }

    const next = page.nextToken;
    if (next === undefined) {
      // Server predates pagination: it returns at most one page of 60 and never a token.
      const full = (page.users || []).length >= LOGIN_PAGE_SIZE;
      return {
        users: [...byUsername.values()], complete: !full, reason: full ? 'server-not-paginated' : undefined,
        pages, duplicatesDropped, aborted: false,
      };
    }
    if (next === null || next === '') {
      return { users: [...byUsername.values()], complete: true, pages, duplicatesDropped, aborted: false };
    }
    if (usedTokens.has(next)) {
      return { users: [...byUsername.values()], complete: false, reason: 'repeated-token', pages, duplicatesDropped, aborted: false };
    }
    if (pages >= maxPages) {
      return { users: [...byUsername.values()], complete: false, reason: 'page-limit', pages, duplicatesDropped, aborted: false };
    }
    usedTokens.add(next);
    token = next;
  }
}
